import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { TrendingUp, Loader2 } from 'lucide-react';
import { useAuth } from './AuthContext';
import Footer from './components/Footer';

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
    <path d="M9.003 18c2.43 0 4.467-.806 5.956-2.18l-2.909-2.26c-.806.54-1.836.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009.003 18z" fill="#34A853"/>
    <path d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
    <path d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.428 0 9.002 0A8.997 8.997 0 00.957 4.958L3.964 7.29c.708-2.127 2.692-3.71 5.036-3.71z" fill="#EA4335"/>
  </svg>
);

const AuthPage = () => {
  const { login, register, loginWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ email: '', password: '', name: '', confirmPassword: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('Login realizado com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerData.password !== registerData.confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    
    if (registerData.password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    setLoading(true);
    try {
      await register(registerData.email, registerData.password, registerData.name);
      toast.success('Conta criada com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    setGoogleLoading(true);
    loginWithGoogle();
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <TrendingUp className="auth-logo" />
          <h1>AtlasIA</h1>
          <p>Consultoria Empresarial Inteligente</p>
        </div>

        <Card className="auth-card">
          <Tabs defaultValue="login">
            <TabsList className="auth-tabs">
              <TabsTrigger value="login" data-testid="login-tab">Entrar</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Criar Conta</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <CardHeader>
                <CardTitle>Entrar na sua conta</CardTitle>
                <CardDescription>Digite suas credenciais para acessar</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Google Login Button */}
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleGoogleLogin}
                  disabled={googleLoading}
                  className="google-btn"
                  style={{
                    width: '100%',
                    marginBottom: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    background: '#fff',
                    color: '#333',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontWeight: '500'
                  }}
                >
                  {googleLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <GoogleIcon />
                  )}
                  Continuar com Google
                </Button>

                <div className="divider" style={{
                  display: 'flex',
                  alignItems: 'center',
                  margin: '1rem 0',
                  gap: '1rem'
                }}>
                  <div style={{ flex: 1, height: '1px', background: '#333' }}></div>
                  <span style={{ color: '#888', fontSize: '0.85rem' }}>ou</span>
                  <div style={{ flex: 1, height: '1px', background: '#333' }}></div>
                </div>

                <form onSubmit={handleLogin} className="auth-form">
                  <div className="form-field">
                    <label>Email</label>
                    <Input
                      type="email"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      placeholder="seu@email.com"
                      required
                      data-testid="login-email"
                    />
                  </div>
                  <div className="form-field">
                    <label>Senha</label>
                    <Input
                      type="password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      placeholder="••••••••"
                      required
                      data-testid="login-password"
                    />
                  </div>
                  <Button type="submit" disabled={loading} className="auth-submit" data-testid="login-submit">
                    {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Entrando...</> : 'Entrar'}
                  </Button>
                </form>
              </CardContent>
            </TabsContent>

            <TabsContent value="register">
              <CardHeader>
                <CardTitle>Criar nova conta</CardTitle>
                <CardDescription>Comece gratuitamente agora</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Google Register Button */}
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleGoogleLogin}
                  disabled={googleLoading}
                  className="google-btn"
                  style={{
                    width: '100%',
                    marginBottom: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    background: '#fff',
                    color: '#333',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontWeight: '500'
                  }}
                >
                  {googleLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <GoogleIcon />
                  )}
                  Continuar com Google
                </Button>

                <div className="divider" style={{
                  display: 'flex',
                  alignItems: 'center',
                  margin: '1rem 0',
                  gap: '1rem'
                }}>
                  <div style={{ flex: 1, height: '1px', background: '#333' }}></div>
                  <span style={{ color: '#888', fontSize: '0.85rem' }}>ou</span>
                  <div style={{ flex: 1, height: '1px', background: '#333' }}></div>
                </div>

                <form onSubmit={handleRegister} className="auth-form">
                  <div className="form-field">
                    <label>Nome (opcional)</label>
                    <Input
                      type="text"
                      value={registerData.name}
                      onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                      placeholder="Seu nome"
                      data-testid="register-name"
                    />
                  </div>
                  <div className="form-field">
                    <label>Email</label>
                    <Input
                      type="email"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      placeholder="seu@email.com"
                      required
                      data-testid="register-email"
                    />
                  </div>
                  <div className="form-field">
                    <label>Senha</label>
                    <Input
                      type="password"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      placeholder="••••••••"
                      required
                      minLength={6}
                      data-testid="register-password"
                    />
                  </div>
                  <div className="form-field">
                    <label>Confirmar Senha</label>
                    <Input
                      type="password"
                      value={registerData.confirmPassword}
                      onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                      placeholder="••••••••"
                      required
                      data-testid="register-confirm"
                    />
                  </div>
                  <Button type="submit" disabled={loading} className="auth-submit" data-testid="register-submit">
                    {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Criando...</> : 'Criar Conta'}
                  </Button>
                </form>
              </CardContent>
            </TabsContent>
          </Tabs>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default AuthPage;
